"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, Loader2, Search, ShieldCheck, X } from "lucide-react";
import { useFocusTrap } from "@/hooks/use-focus-trap";

const ETBIS_SITE_QUERY_URL = "https://etbis.ticaret.gov.tr/tr/SiteSorgulama";

function normalizeQueryUrl(queryUrl?: string): string {
  if (!queryUrl) return ETBIS_SITE_QUERY_URL;

  try {
    const url = new URL(queryUrl);
    if (url.protocol === "http:" || url.protocol === "https:") {
      return url.toString();
    }
  } catch {
    return ETBIS_SITE_QUERY_URL;
  }

  return ETBIS_SITE_QUERY_URL;
}

export function EtbisStatusBrowser({ queryUrl }: { queryUrl?: string }) {
  const [open, setOpen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const modalRef = useFocusTrap(open);
  const resolvedQueryUrl = normalizeQueryUrl(queryUrl);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setIframeLoaded(false);
          setOpen(true);
        }}
        className="group inline-flex h-10 w-full items-center justify-center gap-2 rounded-[var(--sf-radius)] border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-950 focus:outline-none focus:ring-2 focus:ring-[var(--sf-accent)] focus:ring-offset-2 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10"
      >
        <Search className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
        <span className="whitespace-nowrap">ETBİS site durumunu sorgula</span>
      </button>

      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/65 p-3 backdrop-blur-sm sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              ref={(element) => {
                modalRef.current = element;
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="etbis-browser-title"
              className="flex max-h-[88svh] w-full max-w-5xl flex-col overflow-hidden rounded-[var(--sf-radius)] border border-white/15 bg-white shadow-2xl"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 18 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-3 py-3 sm:px-4">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="hidden items-center gap-1.5 sm:flex" aria-hidden="true">
                    <span className="h-3 w-3 rounded-full bg-red-400" />
                    <span className="h-3 w-3 rounded-full bg-amber-400" />
                    <span className="h-3 w-3 rounded-full bg-emerald-400" />
                  </div>
                  <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs text-gray-500 shadow-sm">
                    <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                    <span id="etbis-browser-title" className="truncate font-medium text-gray-700">
                      etbis.ticaret.gov.tr / Site Sorgulama
                    </span>
                  </div>
                </div>
                <a
                  href={resolvedQueryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden h-9 items-center gap-2 rounded-full border border-gray-200 px-3 text-xs font-semibold text-gray-600 transition-colors hover:border-gray-300 hover:bg-white hover:text-gray-950 sm:inline-flex"
                >
                  Resmi sayfayı aç
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-400 transition-colors hover:border-gray-300 hover:bg-white hover:text-gray-950"
                  aria-label="ETBİS sorgulama penceresini kapat"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>

              <div className="relative min-h-[66svh] flex-1 bg-white">
                {!iframeLoaded && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white text-gray-500">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-hidden="true" />
                    <p className="text-sm font-medium">ETBİS sorgulama ekranı yükleniyor...</p>
                    <a
                      href={resolvedQueryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
                    >
                      Resmi sayfayı yeni sekmede aç
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                  </div>
                )}
                <iframe
                  src={resolvedQueryUrl}
                  title="ETBİS kayıtlı site sorgulama"
                  className="h-full min-h-[66svh] w-full border-0"
                  loading="lazy"
                  sandbox="allow-forms allow-scripts allow-same-origin allow-popups"
                  onLoad={() => setIframeLoaded(true)}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
